import { AmbientIllustration } from "@shared/components/AmbientIllustration";
import React from "react";

// Illustration coordinates, not UI dimensions. The shallow isometric plane
// echoes the retail Rewards platforms, with account records in place of coins.
const PLANE = "matrix(.86 .43 -.86 .43 280 126)";
const OUTLINE = "M-112-82H112V82H-112Z";
const CYCLE = "10s";

const ACCOUNT_CSS = `
.account-finalizing { --account-cycle: ${CYCLE}; }
.account-finalizing .account-layer {
    animation: account-layer var(--account-cycle) var(--ease-in-out-cubic) infinite;
}
.account-finalizing .account-content {
    animation: account-content var(--account-cycle) var(--ease-out-cubic) infinite;
}
.account-finalizing .account-slot {
    opacity: 0;
    animation: account-slot var(--account-cycle) var(--ease-out-cubic) var(--slot-delay) infinite;
}
.account-finalizing .account-guide {
    animation: account-guide var(--account-cycle) var(--ease-in-out-cubic) infinite;
}
@keyframes account-layer {
    0%, 12%, 100% { transform: translateY(var(--layer-lift)); }
    38%, 76% { transform: translateY(0); }
    92% { transform: translateY(var(--layer-lift)); }
}
@keyframes account-content {
    0%, 12%, 100% { opacity: .35; }
    38%, 76% { opacity: 1; }
    92% { opacity: .35; }
}
@keyframes account-slot {
    0%, 30% { opacity: 0; transform: translateY(-14px); }
    43%, 65% { opacity: 1; transform: translateY(0); }
    76%, 100% { opacity: 0; transform: translateY(0); }
}
@keyframes account-guide {
    0%, 12%, 100% { opacity: .6; }
    38%, 76% { opacity: 0; }
    92% { opacity: .6; }
}
.account-finalizing[data-motion="static"] .account-layer { transform: none; }
.account-finalizing[data-motion="static"] .account-content,
.account-finalizing[data-motion="static"] .account-slot { opacity: 1; transform: none; }
.account-finalizing[data-motion="static"] .account-guide { opacity: 0; }
@media (prefers-reduced-motion: reduce) {
    .account-finalizing .account-layer { transform: none; }
    .account-finalizing .account-content,
    .account-finalizing .account-slot { opacity: 1; transform: none; }
    .account-finalizing .account-guide { opacity: 0; }
}
`;

interface LayerStyle extends React.CSSProperties {
    "--layer-lift": string;
}

interface SlotStyle extends React.CSSProperties {
    "--slot-delay": string;
}

/** A conceptual account assembly, with no live progress or completion claim. */
export function AccountFinalizingIllustration(): React.ReactElement {
    return (
        <AmbientIllustration
            css={ACCOUNT_CSS}
            className="account-finalizing w-full max-w-lg"
        >
            <svg
                viewBox="0 0 560 310"
                fill="none"
                className="block h-auto w-full"
            >
                {/* Faint registration lines keep the lifted planes grounded. */}
                <g className="stroke-line-base" strokeWidth={1}>
                    <path
                        d="M70 183L280 288 490 183M70 161L280 266 490 161"
                        opacity={0.45}
                    />
                    <g
                        className="account-guide"
                        strokeDasharray="2 5"
                        data-ambient-motion
                    >
                        <path d="M113.16 95.1V167.1M446.84 120.9V192.9M254.2 24.58V96.58M305.8 191.42V263.42" />
                    </g>
                </g>

                {/* Foundation, record layer, and account face settle together. */}
                {[
                    { y: 54, lift: "0px" },
                    { y: 42, lift: "-24px" },
                    { y: 30, lift: "-48px" }
                ].map(({ y, lift }, index) => (
                    <g key={y} transform={`translate(0 ${y})`}>
                        <g
                            className="account-layer"
                            style={{ "--layer-lift": lift } as LayerStyle}
                            data-ambient-motion
                        >
                            <g transform={PLANE} strokeWidth={1}>
                                <path
                                    d="M-112 82H112V-82L118-76V88H-106Z"
                                    className="fill-surface-raised stroke-line-tint-neutral"
                                />
                                <path
                                    d={OUTLINE}
                                    className="fill-surface-ground stroke-line-tint-neutral"
                                />
                                {index < 2 ? (
                                    <path
                                        d="M-94 64H94M-94 52H54"
                                        className="stroke-line-base"
                                    />
                                ) : (
                                    <g
                                        className="account-content"
                                        data-ambient-motion
                                    >
                                        {/* Account header: geometric identity mark and quiet text rules. */}
                                        <rect
                                            x={-92}
                                            y={-62}
                                            width={32}
                                            height={32}
                                            className="fill-surface-tint-ground stroke-line-tint-neutral"
                                        />
                                        <path
                                            d="M-84-46H-68M-76-54V-38"
                                            className="stroke-tint-base"
                                        />
                                        <path
                                            d="M-46-54H52M-46-40H18M-92-12H92"
                                            className="stroke-line-tint-neutral"
                                            strokeWidth={2}
                                        />
                                        {/* Empty slots are abstract future positions, never fake balances. */}
                                        {[-92, -28, 36].map((x, index) => (
                                            <g key={x}>
                                                <rect
                                                    x={x}
                                                    y={6}
                                                    width={56}
                                                    height={52}
                                                    className="stroke-line-base"
                                                    strokeDasharray="3 4"
                                                />
                                                <g
                                                    className="account-slot"
                                                    style={
                                                        {
                                                            "--slot-delay": `${index * 0.45}s`
                                                        } as SlotStyle
                                                    }
                                                    data-ambient-motion
                                                >
                                                    <rect
                                                        x={x}
                                                        y={6}
                                                        width={56}
                                                        height={52}
                                                        className="fill-surface-tint-ground stroke-line-tint-neutral"
                                                    />
                                                    <path
                                                        d={`M${x + 12} 22H${x + 32}M${x + 12} 32H${x + 44}M${x + 12} 42H${x + 36}`}
                                                        className="stroke-line-tint-neutral"
                                                        strokeWidth={2}
                                                    />
                                                </g>
                                            </g>
                                        ))}
                                    </g>
                                )}
                            </g>
                        </g>
                    </g>
                ))}
            </svg>
        </AmbientIllustration>
    );
}
